import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default class ProcessTimeline {
    constructor() {
        this.element = document.querySelector('.process__timeline');
        this.progressBar = this.element.querySelector('[data-progress]');
        this.steps = this.element.querySelectorAll('[data-step]');
        this.steamAnimations = new Map();
        this.icons = this.element.querySelectorAll('[data-icon]');

        this.init();
    }

    init() {
        if (!this.progressBar || !this.steps.length) return;

        this.setupInitialState();
        this.animateProgressBar();
    }

    setupInitialState() {
        // État initial : barre de progression vide
        gsap.set(this.progressBar, {
            scaleY: 0,
            transformOrigin: 'top center'
        });

        // État initial : étapes désactivées
        this.steps.forEach((step) => {
            step.classList.add('is-inactive');
        });

    }

    animateProgressBar() {
        // Animation de la barre de progression liée au scroll
        gsap.to(this.progressBar, {
            scaleY: 1,
            ease: 'none',
            scrollTrigger: {
                trigger: this.element,
                start: 'top 80%',
                end: 'bottom 45%',
                scrub: 1,
                onUpdate: (self) => {
                    this.updateStepsActivation(self.progress);
                }
            }
        });
    }



    updateStepsActivation(progress) {

        // Calcul les steps actives suivant progressBar + lance animation SVG
        const totalSteps = this.steps.length;
        const currentStepIndex = Math.floor(progress * totalSteps);

        this.steps.forEach((step, index) => {
            const wasActive = step.classList.contains('is-active');
            const isNowActive = index <= currentStepIndex;

            if (isNowActive) {
                step.classList.add('is-active');
                step.classList.remove('is-inactive');

                // Si l'étape vient de devenir active, lancer l'animation
                if (!wasActive) {
                    this.animateStepSvg(step);
                }
            } else {
                step.classList.remove('is-active');
                step.classList.add('is-inactive');

                // Si l'étape vient de devenir inactive, arrêter l'animation
                if (wasActive) {
                    this.stopStepSvg(step);
                }
            }
        });
    }

    animateStepSvg = (step) => {
        const stepId = step.getAttribute('data-step')

        switch (parseInt(stepId)) {
            case 1 :
                this.animeCoffeCup(step)
                break
            case 2 :
                this.animeWaveCircles(step)
                break
            case 3 :
                this.animeChatBubbles(step)
                break
            case 4 :
                this.animeSuccessCheck(step)
                break
        }
    }
    animeWaveCircles = (step) => {
        const tl = gsap.timeline({ repeat: -1 });

        ['#circle-1', '#circle-2', '#circle-3'].forEach((selector, index) => {
            gsap.set(selector, {
                y: 11
            })
            tl.to(selector, {
                y: -10,
                duration: 1,
                yoyo: true,
                repeat: -1
            }, index * 0.25);
        });

        this.steamAnimations.set(step, tl);
        return tl;
    }

    animeCoffeCup = (step) => {
        const tl = gsap.timeline({ repeat: -1 });

        // Chaque trait a son propre mouvement ondulant
        tl.to('#steam-1', {
            y: -4,
            x: 1.5,
            opacity: 0.5,
            duration: 1.2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1
        }, 0)
            .to('#steam-2', {
                y: -5,
                x: 1.5,
                opacity: 0.6,
                duration: 1.5,
                ease: 'sine.inOut',
                yoyo: true,
                repeat: -1
            }, 0.3)
            .to('#steam-3', {
                y: -4.5,
                x: 1.5,
                opacity: 0.4,
                duration: 1.8,
                ease: 'sine.inOut',
                yoyo: true,
                repeat: -1
            }, 0.6);

        // Stocker la timeline pour pouvoir l'arrêter
        this.steamAnimations.set(step, tl);

        return tl;
    }

    animeChatBubbles = (step) => {
        const tl = gsap.timeline({ repeat: -1 });

        // Les deux bulles montent et descendent en continu avec un léger décalage
        tl.to('#chat-left', {
            y: -9,
            x: 4,
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1
        }, 0)
            .to('#chat-right', {
                y: 3,
                x: -4,
                opacity: 0.75,
                duration: 1.4,
                ease: 'sine.inOut',
                yoyo: true,
                repeat: -1
            }, 0.4);

        this.steamAnimations.set(step, tl);
        return tl;
    }

    animeSuccessCheck = (step) => {
        const circle = document.querySelector('#success-circle');
        const check = document.querySelector('#success-check');

        if (!circle || !check) return;

        // Calculer la longueur des paths
        const circleLength = circle.getTotalLength();
        const checkLength = check.getTotalLength();

        // Initialiser les paths (invisibles au départ)
        gsap.set(circle, {
            strokeDasharray: circleLength,
            strokeDashoffset: circleLength
        });

        gsap.set(check, {
            opacity: 0,
            strokeDasharray: checkLength,
            strokeDashoffset: checkLength
        });

        // Timeline sans repeat
        const tl = gsap.timeline();

        // 1. Le cercle se dessine
        tl.to(circle, {
            strokeDashoffset: 0,
            duration: 1,
            ease: 'power2.inOut'
        })
            // 2. Le check apparaît
            .to(check, {
                strokeDashoffset: 0,
                duration: .6,
                opacity: 1,
                ease: 'power2.out'
            }, '-=0.2'); // Commence un peu avant la fin du cercle

        this.steamAnimations.set(step, tl);
        return tl;
    }


    stopStepSvg = (step) => {
        const tl = this.steamAnimations.get(step);
        if (tl) {
            tl.kill();
            this.steamAnimations.delete(step);

            const stepId = parseInt(step.getAttribute('data-step'));
            if (stepId === 1) {
                gsap.set(['#steam-1', '#steam-2', '#steam-3'], {
                    y: 0, x: 0, opacity: 1
                });
            } else if (stepId === 2) {
                gsap.set(['#circle-1', '#circle-2', '#circle-3'], {
                    y: 0, scale: 1
                });
            } else if (stepId === 3) {
                gsap.set(['#chat-left', '#chat-right'], {
                    y: 0, x: 0, opacity: 1
                });
            }
        }
    }
    destroy() {
        ScrollTrigger.getAll().forEach(trigger => {
            if (trigger.vars.trigger === this.element ||
                Array.from(this.steps).includes(trigger.vars.trigger)) {
                trigger.kill();
            }
        });
    }
}